import { ModuleEvent } from 'src/app/shared/logging/event-log.service';
import { EVENT_LOG_ENDPOINTS } from './event-log-endpoints';
import { FrontEventLogPartial } from './front-event-log.types';

/** Acciones de bajo valor que no se envían al API. */
const SKIP_ACTIONS = new Set(['step_change', 'locked_today']);

interface MappedEvent {
  partial: FrontEventLogPartial;
  skip?: boolean;
}

function pickTicket(payload?: Record<string, unknown>): string | undefined {
  const tkt = payload?.tkt ?? payload?.ticket;
  return tkt != null ? String(tkt) : undefined;
}

function pickPedido(payload?: Record<string, unknown>): string | undefined {
  const p = payload?.orderNumber ?? payload?.pedido;
  return p != null ? String(p) : undefined;
}

function pickViajeId(payload?: Record<string, unknown>): string | undefined {
  const v = payload?.viajeId ?? payload?.idViaje;
  return v != null ? String(v) : undefined;
}

function pickGuia(payload?: Record<string, unknown>): string | undefined {
  const candidates = [
    payload?.folioNumberGD,
    payload?.numeroGuia,
    payload?.numGuia,
    payload?.nroGuia,
    payload?.NroGuia,
    payload?.nro_guia,
    payload?.guia,
    payload?.GUIA,
    payload?.Nro_Guia,
    payload?.folio_h,
    payload?.FOLIO_H,
    payload?.folio,
  ];
  for (const raw of candidates) {
    if (raw != null && String(raw).trim()) {
      return String(raw).trim();
    }
  }
  return undefined;
}

function pickCodigoError(payload?: Record<string, unknown>): string | undefined {
  if (payload?.codigoError != null) {
    return String(payload.codigoError);
  }
  const code = payload?.code;
  if (code != null && typeof code === 'string') {
    return String(code);
  }
  if (payload?.message != null) {
    const msg = String(payload.message).trim();
    return msg || undefined;
  }
  return undefined;
}

function pickCodigoHttp(payload?: Record<string, unknown>): number | undefined {
  const candidates = [payload?.codigoHttp, payload?.httpStatus, payload?.status];
  for (const raw of candidates) {
    if (raw == null) {
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  const code = payload?.code;
  if (code != null && typeof code === 'number') {
    return code;
  }
  if (typeof code === 'string' && /^\d+$/.test(code)) {
    const n = Number(code);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function pickCorrelationId(payload?: Record<string, unknown>): string | undefined {
  const c = payload?.correlationId;
  return c != null ? String(c) : undefined;
}

function pickEntidad(
  module: string,
  payload?: Record<string, unknown>,
  ticket?: string
): string | undefined {
  if (payload?.entidad != null) {
    return String(payload.entidad);
  }
  if (module === 'firma-digital' || module === 'seguimiento-anonimo') {
    return ticket ? 'FIRMA' : undefined;
  }
  if (module === 'viajes') {
    return ticket ? 'VIAJE' : undefined;
  }
  return ticket ? 'GUIA_DESPACHO' : undefined;
}

function baseFromPayload(
  module: string,
  payload: Record<string, unknown> | undefined,
  extra?: Partial<FrontEventLogPartial>
): Partial<FrontEventLogPartial> {
  const ticket = pickTicket(payload);
  const pedido = pickPedido(payload);
  const viajeId = pickViajeId(payload);
  const guia = pickGuia(payload);
  const usuarioId =
    payload?.usuarioId != null ? String(payload.usuarioId) : undefined;
  const entidad = pickEntidad(module, payload, ticket);
  const entidadId =
    payload?.entidadId != null
      ? String(payload.entidadId)
      : entidad === 'FIRMA'
        ? ticket
        : entidad === 'GUIA_DESPACHO'
          ? guia ?? viajeId
          : viajeId ?? ticket;

  const datosAdicionales = payload ? { ...payload } : undefined;
  if (datosAdicionales && 'correlationId' in datosAdicionales) {
    delete datosAdicionales.correlationId;
  }

  return {
    ticket,
    pedido,
    viajeId,
    usuarioId,
    entidad,
    entidadId,
    correlationId: pickCorrelationId(payload),
    codigoHttp: pickCodigoHttp(payload),
    codigoError: pickCodigoError(payload),
    ...(payload?.endpointOrigen != null
      ? { endpointOrigen: String(payload.endpointOrigen) }
      : {}),
    endpointDestino:
      payload?.endpointDestino != null ? String(payload.endpointDestino) : undefined,
    datosAdicionales,
    ...extra,
  };
}

const MODULE_EVENT_MAP: Record<
  string,
  Record<string, Omit<MappedEvent, 'skip'> & { skip?: boolean }>
> = {
  'firma-digital': {
    submit_start: {
      partial: {
        tipoEvento: 'FIRMA_ENVIO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Inicio de envío de firma digital.',
      },
    },
    queue_canvas: {
      partial: {
        tipoEvento: 'FIRMA_CAPTURADA',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'CAPTURAR_FIRMA',
        mensaje: 'Firma capturada y encolada localmente.',
      },
    },
    queue_paper: {
      partial: {
        tipoEvento: 'FIRMA_CAPTURADA',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'CAPTURAR_FIRMA',
        mensaje: 'Firma en papel registrada localmente.',
      },
    },
    send_canvas: {
      partial: {
        tipoEvento: 'FIRMA_ENVIO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Envío de firma al servidor iniciado.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
      },
    },
    send_success: {
      partial: {
        tipoEvento: 'FIRMA_ENVIADA',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Firma enviada correctamente.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
      },
    },
    submit_confirmed: {
      partial: {
        tipoEvento: 'FIRMA_ENVIADA',
        nivel: 'INFORMATION',
        modulo: 'FIRMA_DIGITAL',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Firma confirmada en servidor.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
      },
    },
    submit_offline: {
      partial: {
        tipoEvento: 'OPERACION_GUARDADA',
        nivel: 'INFORMATION',
        modulo: 'STORE_FORWARD',
        accion: 'GUARDAR_PENDIENTE',
        mensaje: 'Firma guardada para sincronización offline.',
      },
    },
    error: {
      partial: {
        tipoEvento: 'FIRMA_ENVIO_ERROR',
        nivel: 'ERROR',
        modulo: 'FIRMA_DIGITAL',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Error en el flujo de firma digital.',
      },
    },
  },
  checklist: {
    load_start: {
      partial: {
        tipoEvento: 'CHECKLIST_CONSULTA_INICIADA',
        nivel: 'INFORMATION',
        modulo: 'CHECKLIST',
        accion: 'CARGAR_FORMULARIO',
        mensaje: 'Carga de checklist iniciada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.CHECKLIST_FORM,
      },
    },
    load_success: {
      partial: {
        tipoEvento: 'CHECKLIST_CARGADO',
        nivel: 'INFORMATION',
        modulo: 'CHECKLIST',
        accion: 'CARGAR_FORMULARIO',
        mensaje: 'Checklist cargado correctamente.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.CHECKLIST_FORM,
      },
    },
    submit_success: {
      partial: {
        tipoEvento: 'CHECKLIST_ENVIADO',
        nivel: 'INFORMATION',
        modulo: 'CHECKLIST',
        accion: 'ENVIAR_FORMULARIO',
        mensaje: 'Checklist enviado correctamente.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.CHECKLIST_SET,
      },
    },
    error: {
      partial: {
        tipoEvento: 'CHECKLIST_ERROR',
        nivel: 'ERROR',
        modulo: 'CHECKLIST',
        accion: 'ENVIAR_FORMULARIO',
        mensaje: 'Error en checklist.',
      },
    },
  },
  fatiga: {
    load_start: {
      partial: {
        tipoEvento: 'FATIGA_CONSULTA_INICIADA',
        nivel: 'INFORMATION',
        modulo: 'FATIGA',
        accion: 'CARGAR_FORMULARIO',
        mensaje: 'Carga de formulario de fatiga iniciada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.FATIGA_LIST,
      },
    },
    load_success: {
      partial: {
        tipoEvento: 'FATIGA_CARGADO',
        nivel: 'INFORMATION',
        modulo: 'FATIGA',
        accion: 'CARGAR_FORMULARIO',
        mensaje: 'Formulario de fatiga cargado.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.FATIGA_LIST,
      },
    },
    submit_start: {
      partial: {
        tipoEvento: 'FATIGA_ENVIO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'FATIGA',
        accion: 'ENVIAR_FORMULARIO',
        mensaje: 'Envío de formulario de fatiga iniciado.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.FATIGA_INSERT,
      },
    },
    submit_success: {
      partial: {
        tipoEvento: 'FATIGA_ENVIADA',
        nivel: 'INFORMATION',
        modulo: 'FATIGA',
        accion: 'ENVIAR_FORMULARIO',
        mensaje: 'Formulario de fatiga enviado correctamente.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.FATIGA_INSERT,
      },
    },
    locked_today: { skip: true, partial: { tipoEvento: '', nivel: 'INFORMATION', modulo: '', accion: '', mensaje: '' } },
    step_change: { skip: true, partial: { tipoEvento: '', nivel: 'INFORMATION', modulo: '', accion: '', mensaje: '' } },
    error: {
      partial: {
        tipoEvento: 'FATIGA_ERROR',
        nivel: 'ERROR',
        modulo: 'FATIGA',
        accion: 'ENVIAR_FORMULARIO',
        mensaje: 'Error en formulario de fatiga.',
      },
    },
  },
  combustible: {
    list_load: {
      partial: {
        tipoEvento: 'COMBUSTIBLE_LISTADO',
        nivel: 'INFORMATION',
        modulo: 'COMBUSTIBLE',
        accion: 'LISTAR_REGISTROS',
        mensaje: 'Listado de combustible cargado.',
      },
    },
    submit_start: {
      partial: {
        tipoEvento: 'COMBUSTIBLE_ENVIO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'COMBUSTIBLE',
        accion: 'GUARDAR_REGISTRO',
        mensaje: 'Envío de registro de combustible iniciado.',
      },
    },
    submit_success: {
      partial: {
        tipoEvento: 'COMBUSTIBLE_REGISTRO_GUARDADO',
        nivel: 'INFORMATION',
        modulo: 'COMBUSTIBLE',
        accion: 'GUARDAR_REGISTRO',
        mensaje: 'Registro de combustible guardado correctamente.',
      },
    },
    save_error: {
      partial: {
        tipoEvento: 'COMBUSTIBLE_GUARDAR_ERROR',
        nivel: 'ERROR',
        modulo: 'COMBUSTIBLE',
        accion: 'GUARDAR_REGISTRO',
        mensaje: 'Error al guardar registro de combustible.',
      },
    },
    error: {
      partial: {
        tipoEvento: 'COMBUSTIBLE_ERROR',
        nivel: 'ERROR',
        modulo: 'COMBUSTIBLE',
        accion: 'LISTAR_REGISTROS',
        mensaje: 'Error al cargar combustible.',
      },
    },
  },
  auth: {
    login_iniciado: {
      partial: {
        tipoEvento: 'LOGIN_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'AUTENTICACION',
        accion: 'INICIAR_SESION',
        mensaje: 'Inicio de sesión iniciado.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.AUTH_LOGIN,
      },
    },
    login_ok: {
      partial: {
        tipoEvento: 'LOGIN_OK',
        nivel: 'INFORMATION',
        modulo: 'AUTENTICACION',
        accion: 'INICIAR_SESION',
        mensaje: 'Inicio de sesión exitoso.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.AUTH_LOGIN,
      },
    },
    login_error: {
      partial: {
        tipoEvento: 'LOGIN_ERROR',
        nivel: 'ERROR',
        modulo: 'AUTENTICACION',
        accion: 'INICIAR_SESION',
        mensaje: 'Error en inicio de sesión.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.AUTH_LOGIN,
      },
    },
    token_expirado: {
      partial: {
        tipoEvento: 'TOKEN_EXPIRADO',
        nivel: 'WARNING',
        modulo: 'AUTENTICACION',
        accion: 'VALIDAR_SESION',
        mensaje: 'Token de sesión expirado o inválido.',
      },
    },
    logout: {
      partial: {
        tipoEvento: 'LOGOUT',
        nivel: 'INFORMATION',
        modulo: 'AUTENTICACION',
        accion: 'CERRAR_SESION',
        mensaje: 'Sesión cerrada por el usuario.',
      },
    },
  },
  'store-forward': {
    operacion_guardada: {
      partial: {
        tipoEvento: 'OPERACION_GUARDADA',
        nivel: 'INFORMATION',
        modulo: 'STORE_FORWARD',
        accion: 'GUARDAR_PENDIENTE',
        mensaje: 'Operación guardada en cola local.',
      },
    },
    reintento_iniciado: {
      partial: {
        tipoEvento: 'REINTENTO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'STORE_FORWARD',
        accion: 'REINTENTAR',
        mensaje: 'Reintento de sincronización iniciado.',
      },
    },
    operacion_enviada: {
      partial: {
        tipoEvento: 'OPERACION_ENVIADA',
        nivel: 'INFORMATION',
        modulo: 'STORE_FORWARD',
        accion: 'SINCRONIZAR',
        mensaje: 'Operación sincronizada correctamente.',
      },
    },
    error_permanente: {
      partial: {
        tipoEvento: 'ERROR_PERMANENTE',
        nivel: 'ERROR',
        modulo: 'STORE_FORWARD',
        accion: 'SINCRONIZAR',
        mensaje: 'Error permanente en sincronización.',
      },
    },
  },
  conectividad: {
    offline: {
      partial: {
        tipoEvento: 'CONECTIVIDAD_OFFLINE',
        nivel: 'WARNING',
        modulo: 'CONECTIVIDAD',
        accion: 'OFFLINE',
        mensaje: 'Dispositivo sin conectividad de red.',
      },
    },
    online: {
      partial: {
        tipoEvento: 'CONECTIVIDAD_RESTABLECIDA',
        nivel: 'INFORMATION',
        modulo: 'CONECTIVIDAD',
        accion: 'ONLINE',
        mensaje: 'Conectividad de red restablecida.',
      },
    },
    degraded: {
      partial: {
        tipoEvento: 'CONECTIVIDAD_DEGRADADA',
        nivel: 'WARNING',
        modulo: 'CONECTIVIDAD',
        accion: 'DEGRADED',
        mensaje: 'Conectividad degradada.',
      },
    },
  },
  'seguimiento-anonimo': {
    validar_enlace_iniciado: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_VALIDAR_ENLACE_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'VALIDAR_ENLACE',
        mensaje: 'Validación de enlace QR iniciada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.SEGUIMIENTO_VIAJES,
      },
    },
    validar_enlace_ok: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_VALIDAR_ENLACE_OK',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'VALIDAR_ENLACE',
        mensaje: 'Enlace QR validado correctamente.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.SEGUIMIENTO_VIAJES,
      },
    },
    firma_iniciada: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_FIRMA_INICIADA',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Envío de firma anónima iniciado.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.SEGUIMIENTO_FIRMA,
      },
    },
    firma_encolada: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_FIRMA_ENCOLADA',
        nivel: 'WARNING',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'GUARDAR_PENDIENTE',
        mensaje: 'Firma anónima guardada localmente por falta de conectividad.',
      },
    },
    firma_confirmada: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_FIRMA_CONFIRMADA',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'ENVIAR_FIRMA',
        mensaje: 'Firma anónima confirmada en servidor.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.SEGUIMIENTO_FIRMA,
      },
    },
    firma_enviada: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_FIRMA_SINCRONIZADA',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'SINCRONIZAR',
        mensaje: 'Firma anónima pendiente sincronizada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.SEGUIMIENTO_FIRMA,
      },
    },
    reintento_iniciado: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_REINTENTO_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'REINTENTAR',
        mensaje: 'Reintento de firma anónima pendiente.',
      },
    },
    error: {
      partial: {
        tipoEvento: 'SEGUIMIENTO_ERROR',
        nivel: 'ERROR',
        modulo: 'SEGUIMIENTO_ANONIMO',
        accion: 'ERROR',
        mensaje: 'Error en flujo de seguimiento anónimo.',
      },
    },
  },
  actividad: {
    entrada_dashboard: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_DASHBOARD',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó al dashboard.',
      },
    },
    entrada_checklist: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_CHECKLIST',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó al checklist.',
      },
    },
    entrada_despacho_actual: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_DESPACHO_ACTUAL',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó a despachos actuales.',
      },
    },
    entrada_historial: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_HISTORIAL',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó al historial de despachos.',
      },
    },
    entrada_fatiga: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_FATIGA',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó al módulo de fatiga.',
      },
    },
    entrada_combustible: {
      partial: {
        tipoEvento: 'ACTIVIDAD_ENTRADA_COMBUSTIBLE',
        nivel: 'INFORMATION',
        modulo: 'ACTIVIDAD',
        accion: 'NAVEGAR',
        mensaje: 'Usuario ingresó al control de combustible.',
      },
    },
  },
  viajes: {
    cache_warmup_iniciado: {
      partial: {
        tipoEvento: 'VIAJES_CACHE_WARMUP_INICIADO',
        nivel: 'INFORMATION',
        modulo: 'VIAJES',
        accion: 'CACHE_WARMUP',
        mensaje: 'Precarga de caché de viajes iniciada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_LIST,
      },
    },
    cache_warmup_completado: {
      partial: {
        tipoEvento: 'VIAJES_CACHE_WARMUP_COMPLETADO',
        nivel: 'INFORMATION',
        modulo: 'VIAJES',
        accion: 'CACHE_WARMUP',
        mensaje: 'Precarga de caché de viajes completada.',
        endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_LIST,
      },
    },
    no_encontrado: {
      partial: {
        tipoEvento: 'VIAJE_NO_ENCONTRADO',
        nivel: 'WARNING',
        modulo: 'VIAJES',
        accion: 'CONSULTAR_VIAJE',
        mensaje: 'No se encontraron viajes.',
      },
    },
    error: {
      partial: {
        tipoEvento: 'VIAJE_ERROR',
        nivel: 'ERROR',
        modulo: 'VIAJES',
        accion: 'CONSULTAR_VIAJE',
        mensaje: 'Error al consultar viajes.',
      },
    },
  },
};

export function mapModuleEventToApi(event: ModuleEvent): FrontEventLogPartial | null {
  if (SKIP_ACTIONS.has(event.action)) {
    return null;
  }
  const moduleMap = MODULE_EVENT_MAP[event.module];
  if (!moduleMap) {
    return null;
  }
  const entry = moduleMap[event.action];
  if (!entry || entry.skip) {
    return null;
  }

  const payload = event.payload;
  const actionLabel =
    payload?.action != null ? String(payload.action) : event.action;

  return {
    ...entry.partial,
    ...baseFromPayload(event.module, payload, {
      detalleTecnico: payload?.message
        ? String(payload.message)
        : `action=${actionLabel}`,
    }),
  };
}

export function buildDirectEvent(
  module: string,
  action: string,
  partial: FrontEventLogPartial
): FrontEventLogPartial | null {
  const moduleMap = MODULE_EVENT_MAP[module];
  const entry = moduleMap?.[action];
  if (entry?.skip) {
    return null;
  }
  return entry ? { ...entry.partial, ...partial } : partial;
}
